import type { DragEndEvent } from '@dnd-kit/core';
import clamp from 'lodash/clamp';
import {
  type CSSProperties,
  createContext,
  type PropsWithChildren,
  type RefObject,
  useContext,
  useMemo,
  useState,
} from 'react';

import { useResizeObserver } from '@revelio/core';

import {
  DEFAULT_GRID_COLUMNS,
  DEFAULT_GRID_ROWS,
} from '../PageConfigProvider/constants';
import { useTabContentConfig } from '../PageConfigProvider/PageConfigProvider';
import type {
  Position,
  PositionedItem,
} from '../PageConfigProvider/types/item';
import styles from './EditableGrid.module.css';
import type { Direction } from './Resizer';
import {
  buildOccupancyMatrix,
  isAreaFree,
  occupyArea,
  resolveDropPlacement,
  validateBoundaries,
  validatePosition,
} from './utils/grid-layout-utils';
import {
  clampPositionToReportPage,
  getReportInnerBoundsForY,
} from './utils/report-page-boundaries';

type Grid = {
  rows: number;
  columns: number;
  width: number;
  height: number;
  cellWidth: number;
  cellHeight: number;
  trackWidth: number;
  trackHeight: number;
  columnGap: number;
  rowGap: number;
  paddingLeft: number;
  paddingTop: number;
};

type TempResize = Position & {
  id: string;
};

type UpdateItemPosition = (args: {
  item: PositionedItem;
  delta: DragEndEvent['delta'];
  pointerCoordinates?: { x: number; y: number } | null;
}) => void;

type UpdateItemPositions = (args: {
  items: PositionedItem[];
  delta: DragEndEvent['delta'];
}) => void;

const GridContext = createContext<{
  gridItems: PositionedItem[];
  grid: Grid;
  edgeDragging: Direction | null;
  setEdgeDragging: (direction: Direction | null) => void;
  updateItemPosition: UpdateItemPosition;
  updateItemPositions: UpdateItemPositions;
  updateItemSize: (item: PositionedItem, newPosition: Position) => void;
  tempResize: TempResize | null;
  setTempResize: (tempResize: TempResize | null) => void;
  itemDragging: PositionedItem | null;
  setItemDragging: (item: PositionedItem | null) => void;
}>({
  gridItems: [],
  grid: {
    rows: DEFAULT_GRID_ROWS,
    columns: DEFAULT_GRID_COLUMNS,
    width: 0,
    height: 0,
    cellWidth: 0,
    cellHeight: 0,
    trackWidth: 0,
    trackHeight: 0,
    columnGap: 0,
    rowGap: 0,
    paddingLeft: 0,
    paddingTop: 0,
  },
  edgeDragging: null,
  setEdgeDragging: () => undefined,
  updateItemPosition: () => undefined,
  updateItemPositions: () => undefined,
  updateItemSize: () => undefined,
  tempResize: null,
  setTempResize: () => undefined,
  itemDragging: null,
  setItemDragging: () => undefined,
});

type GridProviderProps = PropsWithChildren<{
  children: React.ReactNode;
  items: PositionedItem[];
  gridRows?: number;
  gridColumns?: number;
  isReport?: boolean;
  gridContainerRef?: RefObject<HTMLDivElement>;
}>;

export const GridProvider = ({
  children,
  items,
  gridRows = DEFAULT_GRID_ROWS,
  gridColumns = DEFAULT_GRID_COLUMNS,
  isReport = false,
  gridContainerRef,
}: GridProviderProps) => {
  const wrapperObserver = useResizeObserver();
  const { width: gridContainerWidth, height: gridContainerHeight } =
    useResizeObserver(gridContainerRef);

  const measuredWidth = gridContainerRef
    ? gridContainerWidth
    : wrapperObserver.width;
  const measuredHeight = gridContainerRef
    ? gridContainerHeight
    : wrapperObserver.height;

  const grid: Grid = useMemo(() => {
    const fallbackGrid: Grid = {
      rows: gridRows,
      columns: gridColumns,
      width: measuredWidth,
      height: measuredHeight,
      cellWidth: gridColumns > 0 ? measuredWidth / gridColumns : 0,
      cellHeight: gridRows > 0 ? measuredHeight / gridRows : 0,
      trackWidth: 0,
      trackHeight: 0,
      columnGap: 0,
      rowGap: 0,
      paddingLeft: 0,
      paddingTop: 0,
    };

    const element = gridContainerRef?.current;
    if (!element || typeof window === 'undefined') {
      return fallbackGrid;
    }

    const computed = window.getComputedStyle(element);

    const parsePx = (value: string): number => {
      const parsed = Number.parseFloat(value);
      return Number.isFinite(parsed) ? parsed : 0;
    };

    const paddingLeft = parsePx(computed.paddingLeft);
    const paddingRight = parsePx(computed.paddingRight);
    const paddingTop = parsePx(computed.paddingTop);
    const paddingBottom = parsePx(computed.paddingBottom);
    const columnGap = parsePx(computed.columnGap);
    const rowGap = parsePx(computed.rowGap);

    const innerWidth = Math.max(0, measuredWidth - paddingLeft - paddingRight);
    const innerHeight = Math.max(
      0,
      measuredHeight - paddingTop - paddingBottom
    );

    const totalColumnGaps = gridColumns > 1 ? (gridColumns - 1) * columnGap : 0;
    const totalRowGaps = gridRows > 1 ? (gridRows - 1) * rowGap : 0;

    const trackWidth =
      gridColumns > 0
        ? Math.max(0, (innerWidth - totalColumnGaps) / gridColumns)
        : 0;
    const trackHeight =
      gridRows > 0 ? Math.max(0, (innerHeight - totalRowGaps) / gridRows) : 0;

    const pitchX = trackWidth + columnGap;
    const pitchY = trackHeight + rowGap;

    return {
      rows: gridRows,
      columns: gridColumns,
      width: measuredWidth,
      height: measuredHeight,
      cellWidth: pitchX,
      cellHeight: pitchY,
      trackWidth,
      trackHeight,
      columnGap,
      rowGap,
      paddingLeft,
      paddingTop,
    };
  }, [gridRows, gridColumns, measuredWidth, measuredHeight, gridContainerRef]);

  const [itemDragging, setItemDragging] = useState<PositionedItem | null>(null);
  const [edgeDragging, setEdgeDragging] = useState<Direction | null>(null);

  const gridItems = items;

  const { dispatch } = useTabContentConfig();

  const getPointerGridPoint = (
    pointerCoordinates?: {
      x: number;
      y: number;
    } | null
  ): { x: number; y: number } | null => {
    if (
      !pointerCoordinates ||
      !gridContainerRef?.current ||
      grid.cellWidth <= 0 ||
      grid.cellHeight <= 0
    ) {
      return null;
    }

    const rect = gridContainerRef.current.getBoundingClientRect();
    const localX = pointerCoordinates.x - rect.left - grid.paddingLeft;
    const localY = pointerCoordinates.y - rect.top - grid.paddingTop;

    return {
      x: clamp(Math.floor(localX / grid.cellWidth), 0, grid.columns - 1),
      y: clamp(Math.floor(localY / grid.cellHeight), 0, grid.rows - 1),
    };
  };

  const updateItemPosition: UpdateItemPosition = ({
    item,
    delta,
    pointerCoordinates,
  }) => {
    const deltaXCells = Math.round(delta.x / grid.cellWidth);
    const deltaYCells = Math.round(delta.y / grid.cellHeight);

    const intendedX = clamp(
      item.position.x + deltaXCells,
      0,
      grid.columns - item.position.width
    );
    const intendedY = clamp(
      item.position.y + deltaYCells,
      0,
      grid.rows - item.position.height
    );
    const pointerGridPoint = getPointerGridPoint(pointerCoordinates);
    const placementBaselineY = pointerGridPoint?.y ?? intendedY;

    const positionWithinBoundaries = isReport
      ? (position: Position) =>
          clampPositionToReportPage(position, placementBaselineY)
      : (position: Position) => position;

    const intendedPosition: Position = positionWithinBoundaries({
      ...item.position,
      x: intendedX,
      y: intendedY,
    });

    const yBounds = isReport
      ? getReportInnerBoundsForY(placementBaselineY)
      : null;
    const placement = resolveDropPlacement({
      grid,
      desired: intendedPosition,
      items,
      ignoreItemId: item.id,
      ...(pointerGridPoint && { dropPoint: pointerGridPoint }),
      ...(yBounds && {
        yBounds: { yStart: yBounds.innerStart, yEnd: yBounds.innerEnd },
      }),
      maxNearestOffset: {
        columns: Math.floor(grid.columns / 2),
        rows: Math.floor(grid.rows / 2),
      },
    });

    if (!placement) return;

    const newItems = items.map((prevItem) =>
      prevItem.id === item.id
        ? { ...prevItem, position: placement.position }
        : prevItem
    );
    dispatch({ type: 'SET_ITEMS', payload: newItems });
  };

  const updateItemPositions: UpdateItemPositions = ({
    items: itemsToMove,
    delta,
  }) => {
    if (itemsToMove.length === 0) return;

    const deltaXCells = Math.round(delta.x / grid.cellWidth);
    const deltaYCells = Math.round(delta.y / grid.cellHeight);
    const moveIds = new Set(itemsToMove.map((it) => it.id));

    const newPositions = new Map<string, Position>();
    for (const item of itemsToMove) {
      const intendedX = clamp(
        item.position.x + deltaXCells,
        0,
        grid.columns - item.position.width
      );
      const intendedY = clamp(
        item.position.y + deltaYCells,
        0,
        grid.rows - item.position.height
      );
      const intendedPosition = isReport
        ? clampPositionToReportPage(
            { ...item.position, x: intendedX, y: intendedY },
            intendedY
          )
        : { ...item.position, x: intendedX, y: intendedY };
      if (!validateBoundaries(grid, intendedPosition)) return;
      newPositions.set(item.id, intendedPosition);
    }

    const otherItems = items.filter((it) => !moveIds.has(it.id));
    const matrix = buildOccupancyMatrix(grid, otherItems);

    for (const item of itemsToMove) {
      const newPosition = newPositions.get(item.id);
      if (!newPosition) return;
      if (!isAreaFree(newPosition, grid, matrix)) return;
      occupyArea(newPosition, matrix);
    }

    const updatedItems = items.map((prevItem) => {
      const newPosition = newPositions.get(prevItem.id);
      if (newPosition) {
        return { ...prevItem, position: newPosition };
      }
      return prevItem;
    });
    dispatch({ type: 'SET_ITEMS', payload: updatedItems });
  };

  const updateItemSize = (item: PositionedItem, newPosition: Position) => {
    const prevItems = items;
    const itemId = item.id;

    // Enforce page boundary for report pages
    const adjustedPosition: Position = isReport
      ? clampPositionToReportPage(newPosition, item.position.y)
      : newPosition;

    const newItems = prevItems.map((it) => {
      if (it.id === itemId) {
        return { ...it, position: adjustedPosition };
      }
      return it;
    });

    const isValid = validatePosition(grid, adjustedPosition, newItems, itemId);

    if (isValid) {
      dispatch({ type: 'SET_ITEMS', payload: newItems });
    }

    return;
  };

  const [tempResize, setTempResize] = useState<TempResize | null>(null);

  const heightPctNumber = (gridRows / DEFAULT_GRID_ROWS) * 100;
  const heightPct = `${heightPctNumber}%`;

  return (
    <GridContext.Provider
      value={{
        gridItems,
        grid,
        edgeDragging,
        setEdgeDragging,
        updateItemPosition,
        updateItemPositions,
        updateItemSize,
        tempResize,
        setTempResize,
        itemDragging,
        setItemDragging,
      }}
    >
      <div
        className={styles.gridWrapper}
        data-report={isReport}
        style={
          {
            '--grid-wrapper-height': isReport
              ? '100%'
              : `calc(${heightPct} + 4rem)`,
          } as CSSProperties
        }
        ref={wrapperObserver.containerRef}
      >
        {children}
      </div>
    </GridContext.Provider>
  );
};

export const useGrid = () => {
  const context = useContext(GridContext);
  if (!context) {
    throw new Error('useGrid must be used within a GridProvider');
  }
  return context;
};
